import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import apiClient from '../../services/api';

const Row = ({ label, value, min, max, step, onChange, format }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="text-gray-300">{label}</span>
      <span className="text-purple-300 font-mono">{format ? format(value) : value}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} />
  </div>
);

export const DifficultyEditor = ({ open, onClose, session, onSaved }) => {
  const config = session?.metadata?.engine_config;
  const [speed, setSpeed] = useState(4);
  const [jumpPower, setJumpPower] = useState(13);
  const [gravity, setGravity] = useState(0.65);
  const [lives, setLives] = useState(3);
  const [ramp, setRamp] = useState(1.25);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && config) {
      const p = config.player || {};
      setSpeed(p.speed ?? 4);
      setJumpPower(p.jumpPower ?? 13);
      setGravity(p.gravity ?? 0.65);
      setLives(p.lives ?? 3);
      const scale = config.difficulty?.enemySpeedScale || [1, 1.25];
      setRamp(scale.length > 1 ? Number((scale[1] / (scale[0] || 1)).toFixed(2)) : 1.25);
    }
  }, [open, config]);

  if (!config) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const newConfig = JSON.parse(JSON.stringify(config));
      newConfig.player = { ...(newConfig.player || {}), speed, jumpPower, gravity, lives };
      const numLevels = (newConfig.levels || []).length || 1;
      newConfig.difficulty = {
        ...(newConfig.difficulty || {}),
        enemySpeedScale: Array.from({ length: numLevels }, (_, i) => Number(Math.pow(ramp, i).toFixed(2))),
      };
      const { data } = await apiClient.put(`/ai/sessions/${session.session_id}/engine-config`, {
        config: newConfig,
      });
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save difficulty settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="difficulty-editor-dialog" className="bg-gray-900 border border-purple-500/40 max-w-md shadow-2xl shadow-black/80">
        <DialogHeader>
          <DialogTitle className="text-white">Difficulty Tuner</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            Tune player physics and per-level difficulty scaling. Changes apply instantly and are free.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Row label="Player speed" value={speed} min={2} max={8} step={0.5} onChange={setSpeed} />
          <Row label="Jump power" value={jumpPower} min={8} max={18} step={0.5} onChange={setJumpPower} />
          <Row label="Gravity" value={gravity} min={0.3} max={1.2} step={0.05} onChange={setGravity} format={(v) => v.toFixed(2)} />
          <Row label="Lives" value={lives} min={1} max={5} step={1} onChange={setLives} />
          <Row
            label="Difficulty ramp per level"
            value={ramp}
            min={1}
            max={2}
            step={0.05}
            onChange={setRamp}
            format={(v) => `x${v.toFixed(2)}`}
          />
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="border-white/20 text-gray-300" onClick={onClose}>Cancel</Button>
            <Button
              data-testid="difficulty-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saving ? 'Saving...' : 'Apply Settings'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
